import React, { useState, useEffect } from 'react';
import { getPublicServices } from '../services/api';
import { useNavigate } from 'react-router-dom';

const DynamicServices = () => {
  const [services, setServices] = useState([]);
  const [sectionContent, setSectionContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredButton, setHoveredButton] = useState(false);
  const navigate = useNavigate();

  const SERVICES_PER_PAGE = 6;

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await getPublicServices();
      console.log('Services API Response:', response);
      
      let servicesData = [];
      if (response && response.data && Array.isArray(response.data)) {
        servicesData = response.data;
      } else if (response && Array.isArray(response)) {
        servicesData = response;
      } else if (response && response.services && Array.isArray(response.services)) {
        servicesData = response.services;
      }
      
      const activeServices = servicesData
        .filter(s => s.isActive !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setServices(activeServices);
      console.log('Services loaded:', activeServices.length);
      
      const sectionResponse = await fetch('https://inteliheads.in/api/cms/public/content/services');
      const sectionDataResponse = await sectionResponse.json();
      if (sectionDataResponse && sectionDataResponse.data) {
        setSectionContent(sectionDataResponse.data);
      } else if (sectionDataResponse && sectionDataResponse._id) {
        setSectionContent(sectionDataResponse);
      }
      
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getServiceIcon = (serviceName, customIcon) => {
    if (customIcon && customIcon !== 'fas fa-cogs') {
      return customIcon;
    }
    
    const name = serviceName.toLowerCase();
    if (name.includes('consulting')) return '💼';
    if (name.includes('workforce') || name.includes('training')) return '👨‍🏫';
    if (name.includes('msme') || name.includes('transformation')) return '🏭';
    if (name.includes('support')) return '🎧';
    if (name.includes('development')) return '💻';
    if (name.includes('integration')) return '🔗';
    return '⚙️';
  };

  const getPriceDisplay = (price) => {
    if (!price) return 'Contact for pricing';
    return price;
  };

  const handleViewDetails = (service) => {
    navigate(`/resource/services/${service._id}`, { 
      state: { item: service, type: 'services' } 
    });
  };

  const handleViewAllServices = () => {
    navigate('/resources', { state: { activeTab: 'services' } });
  };

  const displayedServices = services.slice(0, SERVICES_PER_PAGE);
  const showButton = services.length > 0;

  if (loading) {
    return (
      <section style={{ padding: '5rem 0', textAlign: 'center', background: '#F7F9FC' }}>
        <div className="container">
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2C7A7B' }}></i>
          <p style={{ marginTop: '1rem', color: '#5A6E7F' }}>Loading services...</p>
        </div>
      </section>
    );
  }

  const title = sectionContent?.title || 'Expert Services & Consulting';
  const subtitle = sectionContent?.subtitle || 'End-to-end support from strategic consulting to hands-on implementation.';
  const backgroundColor = sectionContent?.backgroundColor || '#F7F9FC';
  const buttonText = sectionContent?.buttonText || 'Browse All Services';

  return (
    <section id="services" style={{
      padding: 'clamp(3rem, 8vw, 5rem) 0',
      background: backgroundColor,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 1000'%3E%3Cpath fill='%230A192F' d='M0,0 L1000,0 L1000,1000 L0,1000 Z M200,200 L800,200 L800,800 L200,800 Z'/%3E%3Ccircle cx='500' cy='500' r='150'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
        backgroundSize: '50px'
      }} />
      
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: 'clamp(2rem, 6vw, 4rem)'
        }}>
          <h2 style={{
            fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
            marginBottom: '0.5rem',
            color: '#0A192F',
            fontWeight: 700,
            letterSpacing: '-0.02em'
          }}>
            {title}
          </h2>
          
          <div style={{
            width: '60px',
            height: '3px',
            background: '#2C7A7B',
            margin: '1rem auto',
            borderRadius: '2px'
          }} />
          
          <p style={{
            fontSize: 'clamp(1rem, 4vw, 1.125rem)',
            color: '#5A6E7F',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            {subtitle}
          </p>

          {/* Browse All Services Button */}
          {showButton && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginTop: '2rem'
            }}>
              <button
                onClick={handleViewAllServices}
                onMouseEnter={() => setHoveredButton(true)}
                onMouseLeave={() => setHoveredButton(false)}
                style={{
                  position: 'relative',
                  padding: '0.8rem 2rem',
                  background: hoveredButton ? 'transparent' : 'linear-gradient(135deg, #2C7A7B 0%, #236B6C 100%)',
                  color: hoveredButton ? '#2C7A7B' : 'white',
                  border: hoveredButton ? '2px solid #2C7A7B' : 'none',
                  borderRadius: '50px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  overflow: 'hidden',
                  boxShadow: hoveredButton ? 'none' : '0 4px 15px rgba(44,122,123,0.3)'
                }}
              >
                <span style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: hoveredButton ? 'shimmer 1.5s infinite' : 'none'
                }} />
                <i className="fas fa-cogs" style={{ fontSize: '1rem' }}></i>
                <span>{buttonText}</span>
                <i className="fas fa-arrow-right" style={{ 
                  fontSize: '0.85rem',
                  transition: 'transform 0.3s ease',
                  transform: hoveredButton ? 'translateX(5px)' : 'translateX(0)'
                }}></i>
              </button>
            </div>
          )}
        </div>

        {services.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', background: '#F7F9FC', borderRadius: '24px' }}>
            <i className="fas fa-cogs" style={{ fontSize: '4rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
            <p style={{ color: '#5A6E7F', fontSize: '1.1rem' }}>No services available at the moment.</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 'clamp(1.5rem, 3vw, 2rem)',
            marginBottom: 'clamp(2rem, 5vw, 3rem)'
          }}>
            {displayedServices.map((service, index) => (
              <div
                key={service._id || index}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                style={{
                  background: 'white',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '1px solid rgba(44,122,123,0.1)',
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                  e.currentTarget.style.borderColor = '#2C7A7B';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(44,122,123,0.1)';
                }}
              >
                {/* Image Container */}
                <div style={{
                  position: 'relative',
                  height: '220px',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #F7F9FC 0%, #E2E8F0 100%)'
                }}>
                  {service.imageUrl ? (
                    <img 
                      src={getFullImageUrl(service.imageUrl)} 
                      alt={service.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<div style="display: flex; align-items: center; justifyContent: center; height: 100%;"><span style="font-size: 4rem;">${getServiceIcon(service.name, service.icon)}</span></div>`;
                      }}
                    />
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      fontSize: '4rem'
                    }}>
                      {getServiceIcon(service.name, service.icon)}
                    </div>
                  )}
                  
                  {/* Category Badge */}
                  {service.category && (
                    <div style={{
                      position: 'absolute',
                      top: '1rem',
                      left: '1rem',
                      background: 'rgba(44,122,123,0.9)',
                      backdropFilter: 'blur(8px)',
                      padding: '0.3rem 0.8rem',
                      borderRadius: '20px',
                      fontSize: '0.7rem',
                      color: 'white',
                      fontWeight: 600,
                      letterSpacing: '0.5px'
                    }}>
                      {service.category}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: '#0A192F',
                    marginBottom: '0.5rem',
                    lineHeight: '1.3'
                  }}>
                    {service.name}
                  </h3>

                  {/* Price */}
                  {service.price && (
                    <div style={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#2C7A7B',
                      marginBottom: '0.75rem'
                    }}>
                      {getPriceDisplay(service.price)}
                    </div>
                  )}

                  {/* Description */}
                  <p style={{
                    color: '#5A6E7F',
                    lineHeight: '1.6',
                    fontSize: '0.85rem',
                    marginBottom: '1rem',
                    flex: 1
                  }}>
                    {service.description && service.description.length > 120 
                      ? `${service.description.substring(0, 120)}...` 
                      : service.description || 'No description available'}
                  </p>

                  {/* Features Preview */}
                  {service.features && service.features.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        {service.features.slice(0, 2).map((feature, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            fontSize: '0.7rem',
                            color: '#5A6E7F',
                            background: '#F7F9FC',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px'
                          }}>
                            <i className="fas fa-check-circle" style={{ color: '#2C7A7B', fontSize: '0.6rem' }}></i>
                            <span>{feature.length > 25 ? `${feature.substring(0, 25)}...` : feature}</span>
                          </div>
                        ))}
                      </div>
                      {service.features.length > 2 && (
                        <div style={{ fontSize: '0.65rem', color: '#94A3B8' }}>
                          +{service.features.length - 2} more features
                        </div>
                      )}
                    </div>
                  )}

                  {/* View Details Button */}
                  <button
                    onClick={() => handleViewDetails(service)}
                    style={{
                      width: '100%',
                      marginTop: '1rem',
                      padding: '0.7rem 1.2rem',
                      background: 'linear-gradient(135deg, #2C7A7B 0%, #236B6C 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(44,122,123,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    View Details <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>
    </section>
  );
};

export default DynamicServices;